import { FieldValues } from 'react-hook-form';
import { SelectedOptionProps } from './props';
import { useEffect, useMemo, useState } from 'react';
import styles from './styles.module.scss';
import { Input } from '@starsoft/common/components';

/**
 * Component that renders the selected option in an autocomplete menu selector
 *
 * @template T - Type extending FieldValues from react-hook-form
 * @template V - Type of the option value
 *
 * @param {Object} props - Component props
 * @param {V} props.value - Currently selected value
 * @param {boolean} props.disabled - Whether the selector is disabled
 * @param {Array<T>} props.options - Array of available options
 * @param {Function} props.getOptionValue - Function to get value from an option
 * @param {Function} props.getOptionLabel - Function to get display label from an option
 * @param {Object} props.customComponents - Custom component overrides
 * @param {boolean} props.open - Whether the options drawer is open
 * @param {boolean} props.isContainerColor - Whether to use container background color
 * @param {Function} props.setOpen - Function to toggle options drawer
 * @param {boolean} props.loading - Whether options are loading
 * @returns {JSX.Element} Selected option display component
 */
export default function SelectedOption<T extends FieldValues, V>({
  value,
  disabled,
  options,
  getOptionValue,
  getOptionLabel,
  customComponents,
  open,
  isContainerColor,
  setOpen,
  loading,
}: SelectedOptionProps<T, V>) {
  // State to store the currently selected option object
  const [selectedOption, setSelectedOption] = useState<T | undefined>();

  // Memoized lookup of the option object matching the selected value
  const foundOption: T | undefined = useMemo(
    () =>
      options?.find(option => {
        return getOptionValue(option) === value;
      }),
    [getOptionValue, options, value],
  );

  // Get the display label for the selected option
  const selectedLabel: string = selectedOption
    ? getOptionLabel(selectedOption)
    : '';

  /**
   * Updates the selected option state when found option changes
   */
  function onChangeOption() {
    if (!foundOption) {
      return;
    }

    setSelectedOption(foundOption);
  }

  // Update selected option when found option changes
  useEffect(onChangeOption, [foundOption]);

  /**
   * Toggles the options drawer open/closed
   */
  function toggle() {
    setOpen(prev => !prev);
  }

  // Show loading state
  if (loading) {
    if (!!customComponents?.customSkeletonInput) {
      return customComponents?.customSkeletonInput();
    }

    return <Input isLoading isContainerColor={isContainerColor} />;
  }

  // Use custom input component if provided
  if (!!customComponents?.customInput) {
    return customComponents.customInput({
      handleOpen: toggle,
      open,
      option: selectedOption,
    });
  }

  // Default selected option display
  return (
    <div
      className={`${styles.container} ${disabled ? styles['container--disabled'] : ''} ${
        isContainerColor ? styles['container--container-color'] : ''
      }`}
      onClick={toggle}
    >
      <p>{selectedLabel}</p>
      <i className="fas fa-chevron-down" />
    </div>
  );
}
